import { useDataGrid } from 'components/devexpress/Grid/dx-grid.ts';
import { Input } from 'components/uikit/inputs/Input.tsx';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm.ts';
import React, { useEffect, useMemo, useState } from 'react';
import formStyles from 'components/form/form.module.scss';
import pageStyles from './WarrantyRequestHistoryPage.module.scss';
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'react-router-url-params';
import { Loading } from 'components/uikit/suspense/Loading.tsx';
import clsx from 'clsx';
import { Breadcrumbs } from 'components/uikit/Breadcrumbs.tsx';
import SortArrow from 'assets/icons/sort-arrow.svg?react';
import { WarrantyRequestSummaryQuery } from 'services/api/api-client.ts';
import {
  pagingSortingQueryParams,
  pagingSortingToBackendRequest,
} from 'helpers/pagination-helper.ts';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import { Button } from 'components/uikit/buttons/Button.tsx';
import { Accordion } from './components/Accordion.tsx';
import { AppPagination } from 'components/uikit/pagination/AppPagination.tsx';
import ArrowDown from 'assets/icons/arrow-down-selector.svg?react';
import { MenuItem, Select } from '@mui/material';
import { useWatch } from 'react-hook-form';
import { sub } from 'date-fns';
import { FeatureFlags } from '../../../application/constants/env-variables.ts';
import { Checkbox } from '../../../components/uikit/inputs/Checkbox.tsx';
import { NegativeActionText } from '../../../components/uikit/NegativeActionText.tsx';
import { IconButton } from '../../../components/uikit/IconButton.tsx';
import Cross from 'assets/icons/close-button-small.svg?react';
import { TablePlaceholder } from '../../../components/uikit/table/TablePlaceholder.tsx';
import { useAnalytics } from 'helpers/analytics.ts';
import { tDynamic } from '../../../helpers/tDynamic.ts';
import { abasUnavailableUrn, ErrObj } from '../../../helpers/abas-helper.ts';
import { AbasPlaceholder } from '../../../components/uikit/AbasPlaceholder.tsx';

type FormParams = {
  amountOfPeriods: number | null;
  periodType: string;
  orderNumber: string;
  reference: string;
  isOnlyRejectedParts: boolean;
};

export type PaginationProps = {
  page: number;
  perPage: number;
  totalCount: number;
  pageSizes: number[];
  changePagination: (pagination: { page?: number; perPage?: number }) => void;
};

const pageSizes = [10, 50, 100];
const columnsOfOuterTable: {
  translateKey: string;
  serverKey: string;
}[] = [
  {
    serverKey: 'orderId',
    translateKey: 'table_order_number',
  },
  {
    serverKey: 'from',
    translateKey: 'table_date_from',
  },
  {
    serverKey: 'reference',
    translateKey: 'table_reference',
  },
  {
    serverKey: 'order',
    translateKey: 'table_order',
  },
  {
    serverKey: '',
    translateKey: 'parts_count',
  },
  {
    serverKey: '',
    translateKey: 'status',
  },
];

export const WarrantyRequestHistoryCategory = 'Warranty request history';

export const WarrantyRequestHistoryPage = () => {
  const i18n = useScopedTranslation('Page.WarrantyHistoryPage');
  const [openedOrders, setOpenedOrders] = useState<Set<string>>(new Set([]));
  const [hoveredHeader, setHoveredHeader] = useState<string | null>(null);
  const [isSelectVisible, setIsSelectVisible] = useState(false);

  const periodOptions = ['year', 'month', 'week', 'day'];

  useDataGrid();
  const [params, setParams] = useQueryParams({
    orderNumber: StringParam,
    amountOfPeriods: NumberParam,
    periodType: StringParam,
    reference: StringParam,
    isOnlyRejectedParts: BooleanParam,
    ...pagingSortingQueryParams(pageSizes[0]),
    sortBy: withDefault(StringParam, 'from'),
    desc: withDefault(BooleanParam, true),
  });

  const { trackEvent } = useAnalytics();
  const form = useAdvancedForm<FormParams>(
    async (data) => {
      setParams({
        ...params,
        ...data,
        page: 1,
      });
    },
    {
      defaultValues: {
        orderNumber: params.orderNumber ?? '',
        reference: params.reference ?? '',
        periodType: params.periodType ?? periodOptions[0],
        amountOfPeriods:
          params.amountOfPeriods === undefined ? 1 : params.amountOfPeriods,
        isOnlyRejectedParts: params.isOnlyRejectedParts ?? false,
        ...pagingSortingToBackendRequest(params),
      },
    },
  );

  const [
    periodType,
    amountOfPeriods,
    isOnlyRejectedParts,
    orderNumber,
    reference,
  ] = useWatch({
    control: form.control,
    name: [
      'periodType',
      'amountOfPeriods',
      'isOnlyRejectedParts',
      'orderNumber',
      'reference',
    ],
  });

  const getOrderedFrom = useMemo(() => {
    const amountOfPeriodsParam = params.amountOfPeriods;
    if (amountOfPeriodsParam === null) return null;
    const amount =
      amountOfPeriodsParam === undefined ? 1 : amountOfPeriodsParam;
    const period = params.periodType ?? periodOptions[0];

    if (period && typeof amount === 'number') {
      return sub(new Date(), { [period + 's']: amount });
    }
    return null;
  }, [params.periodType, params.amountOfPeriods]);

  const warrantyRequestQuery = WarrantyRequestSummaryQuery.useGetQuery(
    {
      orderedFrom: getOrderedFrom,
      orderNumber: params.orderNumber,
      reference: params.reference,
      isOnlyWithRejectedParts: params.isOnlyRejectedParts ?? undefined,
      ...pagingSortingToBackendRequest(params),
    },
    {
      throwOnError: false,
      // staleTime: 10 * 1000,
      placeholderData: (previousData) => previousData,
    },
  );

  const data = warrantyRequestQuery.data;

  const handleClickAccordion = (key: string) => {
    const copy = new Set(openedOrders);

    trackEvent({
      category: WarrantyRequestHistoryCategory,
      action: 'Click table row group',
      name: `Table group key: ${key}`,
    });

    if (copy.has(key)) {
      copy.delete(key);
    } else {
      copy.add(key);
    }
    setOpenedOrders(copy);
  };

  const changePagination = (pagination: {
    page?: number;
    perPage?: number;
  }) => {
    setParams((prevParams) => ({
      ...prevParams,
      ...(pagination.page !== undefined && { page: pagination.page }),
      ...(pagination.perPage !== undefined && { perPage: pagination.perPage }),
    }));
  };

  const paginationProps: PaginationProps = useMemo(() => {
    return {
      page: params.page,
      perPage: params.perPage,
      totalCount: data?.countOfRecords ?? 0,
      pageSizes: pageSizes,
      changePagination: changePagination,
    };
  }, [params, warrantyRequestQuery?.data?.countOfRecords]);

  const changeSortTable = (key: string) => {
    const previousKey = params.sortBy;

    setParams((prevParams) => ({
      ...prevParams,
      sortBy: key,
      desc: previousKey === key ? !params.desc : false,
      page: 1,
    }));
  };

  useEffect(() => {
    const closeFunc = () => {
      setIsSelectVisible(false);
    };

    document.body.onscroll = closeFunc;

    return () => {
      document.body.onscroll = null;
    };
  }, []);

  const resetFilters = () => {
    form.reset({
      orderNumber: '',
      reference: '',
      periodType: periodOptions[0],
      amountOfPeriods: 1,
      isOnlyRejectedParts: false,
    });

    void form.handleSubmitDefault();
  };

  const isResetButtonShow =
    (!!params.amountOfPeriods && params.amountOfPeriods !== 1) ||
    (!!params.periodType && params.periodType !== periodOptions[0]) ||
    !!params.orderNumber ||
    !!params.reference ||
    !!params.isOnlyRejectedParts;

  const errObj = warrantyRequestQuery?.error as ErrObj;
  const isExternalServiceUnavailable =
    errObj?.response?.type === abasUnavailableUrn;

  const isPlaceholderVisible =
    !data?.records.length &&
    !warrantyRequestQuery.isLoading &&
    !warrantyRequestQuery.isPlaceholderData &&
    !isExternalServiceUnavailable;

  return (
    <div data-test-id={'warranty-request-history-container'}>
      <Breadcrumbs />
      <Loading
        loading={
          // TODO: make it isLoading and transfer getOrderedFrom to backend
          warrantyRequestQuery.isFetching ||
          warrantyRequestQuery.isPlaceholderData
        }
      >
        <form onSubmit={form.handleSubmitDefault}>
          <div
            className={clsx(
              formStyles.rowContainer,
              formStyles.filterContainer,
            )}
          >
            <div
              className={clsx(
                formStyles.rowContainer,
                pageStyles.alignItemsUnset,
              )}
            >
              <Input
                testId={'time-interval-input'}
                className={clsx(pageStyles.width84)}
                leftHelperText={i18n.t('orders_for_last')}
                {...form.register('amountOfPeriods')}
                endAdornment={
                  <div>
                    {amountOfPeriods && (
                      <IconButton
                        type={'destroy'}
                        onClick={() => form.setValue('amountOfPeriods', null)}
                      >
                        <Cross />
                      </IconButton>
                    )}
                  </div>
                }
                endAdornmentClassname={pageStyles.endAdornmentClassname}
              />
              <Select
                open={isSelectVisible}
                onClick={() => {
                  setIsSelectVisible(true);
                }}
                onClose={(e) => {
                  e.stopPropagation();
                  setIsSelectVisible(false);
                }}
                value={periodType}
                onChange={(e) => form.setValue('periodType', e.target.value)}
                className={formStyles.select}
                IconComponent={ArrowDown}
                MenuProps={{
                  PaperProps: {
                    className: formStyles.selectBody,
                  },
                  disableScrollLock: true,
                }}
              >
                {periodOptions.map((periodOption) => (
                  <MenuItem key={periodOption} value={periodOption}>
                    {tDynamic(i18n, periodOption)}
                  </MenuItem>
                ))}
              </Select>
              <Input
                maxLength={10}
                testId={'order-number-input'}
                className={clsx(pageStyles.width128)}
                containerClassName={formStyles.marginLeft8}
                leftHelperText={i18n.t('order_number')}
                {...form.register('orderNumber')}
                endAdornment={
                  <div>
                    {orderNumber && (
                      <IconButton
                        type={'destroy'}
                        onClick={() => form.setValue('orderNumber', '')}
                      >
                        <Cross />
                      </IconButton>
                    )}
                  </div>
                }
                endAdornmentClassname={pageStyles.endAdornmentClassname}
              />
              {FeatureFlags.isReferenceSearchEnabled() && (
                <Input
                  testId={'reference-input'}
                  className={clsx(pageStyles.width160)}
                  containerClassName={formStyles.marginLeft8}
                  leftHelperText={i18n.t('table_reference')}
                  {...form.register('reference')}
                  endAdornment={
                    <div>
                      {reference && (
                        <IconButton
                          type={'destroy'}
                          onClick={() => form.setValue('reference', '')}
                        >
                          <Cross />
                        </IconButton>
                      )}
                    </div>
                  }
                  endAdornmentClassname={pageStyles.endAdornmentClassname}
                />
              )}
              <Checkbox
                testId={'rejected-parts-checkbox'}
                helperText={i18n.t('only_rejected_parts')}
                containerClassName={formStyles.marginLeft8}
                isChecked={isOnlyRejectedParts}
                onChange={(e) =>
                  form.setValue('isOnlyRejectedParts', e.target.checked)
                }
              />
              <Button
                type={'submit'}
                className={clsx(
                  formStyles.actionButton,
                  formStyles.marginLeft8,
                )}
                title={i18n.t('search_button')}
                testId={'filters-apply-button'}
              />
              {isResetButtonShow && (
                <NegativeActionText
                  text={i18n.t('reset')}
                  onClickHandler={resetFilters}
                  containerClassName={pageStyles.resetButton}
                  testId={'filters-reset-button'}
                />
              )}
            </div>
          </div>
        </form>
        {!isExternalServiceUnavailable && (
          <>
            <div className={pageStyles.headerContainer}>
              {columnsOfOuterTable.map((column, index) => {
                const wasSortedByThisColumn =
                  params.sortBy === column.serverKey;
                const wasHoveredThisColumn = hoveredHeader === column.serverKey;
                const isDescSorting = wasSortedByThisColumn && params.desc;

                return (
                  <div
                    key={column.translateKey as string}
                    className={clsx(
                      pageStyles.headerTitle,
                      pageStyles.paddingLeftRight16,
                      index === columnsOfOuterTable.length - 1
                        ? pageStyles.width70
                        : index === columnsOfOuterTable.length - 2
                        ? pageStyles.width117
                        : pageStyles.flex1,
                      column.serverKey && pageStyles.pointer,
                      wasSortedByThisColumn && pageStyles.headerTitleActive,
                      pageStyles.rowContainerSorting,
                    )}
                    onClick={() => {
                      if (column.serverKey) {
                        changeSortTable(column.serverKey);
                      }
                    }}
                    onMouseEnter={() =>
                      column.serverKey
                        ? setHoveredHeader(column.serverKey)
                        : null
                    }
                    onMouseLeave={() => setHoveredHeader(null)}
                  >
                    <div> {tDynamic(i18n, column.translateKey)}</div>
                    <SortArrow
                      className={clsx(
                        !isDescSorting && pageStyles.transform180,
                        wasHoveredThisColumn &&
                          !wasSortedByThisColumn &&
                          pageStyles.sortHover,
                        !wasHoveredThisColumn &&
                          !wasSortedByThisColumn &&
                          pageStyles.displayNone,
                      )}
                    />
                  </div>
                );
              })}
            </div>
            <Accordion
              handleClickAccordion={handleClickAccordion}
              openedOrders={openedOrders}
              data={data?.records ?? []}
            />
          </>
        )}
        {isExternalServiceUnavailable && (
          <AbasPlaceholder containerClassname={pageStyles.marginTop152} />
        )}
        {isPlaceholderVisible && (
          <TablePlaceholder testId={'history-placeholder'} />
        )}
        <AppPagination {...paginationProps} />
      </Loading>
    </div>
  );
};
